/**
 * Do two objects overlap VERTICALLY — the one place that arithmetic lives.
 *
 * Lifted out of `functions/features/post-processing/collision-primitives.js`
 * so `shared-lib` can use it: shared-lib must not require upward into
 * `functions/`. That file now re-exports from here, so this is one copy
 * replacing two rather than a new one.
 *
 * MISSING HEIGHT IS A POLICY, NOT A DETAIL, and the two callers need
 * opposite answers:
 *
 *  - The collision resolvers ask "do these two bodies interpenetrate". A
 *    dimensionless object is a flat point there, so it overlaps nothing.
 *    That is `missingHeight: 'point'`, the historical behaviour, and SCC's
 *    chair rules and CR's `clearanceBodyObstacles` depend on it.
 *  - A floor-clearance test asks "is anything in the way". An object with no
 *    recorded height is unknown, not absent, and treating it as a point
 *    silently stops it being an obstacle — including a user-marked immovable
 *    closet, whose whole purpose is to be one. That is
 *    `missingHeight: 'full'`, the same stance `wallYOverlaps` takes for a
 *    wall with no finite height.
 *
 * `yRangesOverlap` (`functions/features/post-processing/helpers.js`) is a
 * fifth copy and SHOULD fold into this one — but it uses `> 0` where this
 * uses `> 0.01`, and its one caller (`other-rooms.js`, the storage
 * shrink-to-fit collider) runs inside `processStructure`. Migrating it
 * silently changed which near-tangent objects pressure a storage unit to
 * shrink, with no `processStructure` version bump. Fold it in deliberately,
 * with the bump, rather than as a side effect of something else.
 *
 * `objectsAreTouching` (./objectsTouch.js) keeps its own inline form on
 * purpose: it defaults a missing dimension to 0.5 m, and changing that would
 * move real behaviour in `tub-surround/bookend.js` and the staging surface
 * pass. Do not "unify" them.
 */

/** Overlap threshold: below this the two merely graze. */
const Y_OVERLAP_EPS = 0.01;

/**
 * @param {{center?:{y?:number}, dimensions?:{y?:number}}} a
 * @param {{center?:{y?:number}, dimensions?:{y?:number}}} b
 * @param {{missingHeight?: 'point'|'full'}} [opts]
 * @returns {boolean}
 */
function objectsYOverlap(a, b, opts = {}) {
  const full = opts.missingHeight === 'full';
  const ah = a?.dimensions?.y;
  const bh = b?.dimensions?.y;
  // "Unknown height" can only mean "assume it is in the way" for the object
  // being tested AS an obstacle (`b`); a finite 0 is a real measurement. The
  // probe `a` with no height is a point, never a thing that blocks the whole
  // room — the guard used to fire on either operand.
  if (full && !Number.isFinite(bh)) return true;
  const ay = a?.center?.y ?? 0;
  const by = b?.center?.y ?? 0;
  const aH = Number.isFinite(ah) ? ah : 0;
  const bH = Number.isFinite(bh) ? bh : 0;
  const overlapDepth =
    Math.min(ay + aH / 2, by + bH / 2) - Math.max(ay - aH / 2, by - bH / 2);
  return overlapDepth > Y_OVERLAP_EPS;
}

module.exports = { objectsYOverlap, Y_OVERLAP_EPS };
